import { useMemo, useState } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import { Popover, PopoverTrigger, PopoverContent } from "../ui/popover";
import {
  Command, CommandInput, CommandList, CommandEmpty,
  CommandGroup, CommandItem,
} from "../ui/command";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "../ui/select";
import {
  Link2, Link2Off, Trash2, ChevronsUpDown, Check, Package, Plus, AlertTriangle, X,
} from "lucide-react";
import { formatCurrency } from "../../lib/utils";
import { cn } from "../../lib/utils";
import { computeIngredientCost } from "../../lib/units";

/**
 * A single editable row of a recipe ingredient with optional
 * link to an inventory ingredient + format selector.
 *
 * Data shape persisted in `ai_ingredients[i]`:
 *   {
 *     name: string,            // always present (display)
 *     quantity: number,        // in base unit
 *     unit: string,            // base unit (g/kg/ml/L/ud…)
 *     estimated_cost: number,
 *     ingredient_id?: string,  // link to inventory (optional)
 *     format_id?: string,      // chosen format (optional)
 *     format_qty?: number,     // how many format units (e.g. 2 copas)
 *     format_label?: string,   // "copa · 150 ml" (display only)
 *     _display_quantity?: number, // chef's original qty (pre-conversion)
 *     _display_unit?: string,     // chef's original unit (pre-conversion)
 *     _cost_missing?: boolean,    // inventory cost_per_unit == 0 flag
 *   }
 *
 * `suggestion` (optional): from backend auto-link Guard 1. Shape:
 *   { reason: "unit_mismatch", recipe_unit, inventory_unit,
 *     inventory_ingredient_id, inventory_name }
 */
export function RecipeIngredientRow({
  ingredient,
  index,
  inventoryIngredients = [],
  onChange,
  onRemove,
  onQuickAdd,
  isEditing,
  suggestion = null,
  onDismissSuggestion,
}) {
  const [open, setOpen] = useState(false);
  const hasUnitMismatch = suggestion?.reason === "unit_mismatch";

  const linkedIngredient = useMemo(() => {
    if (!ingredient?.ingredient_id) return null;
    return inventoryIngredients.find(i => i.ingredient_id === ingredient.ingredient_id) || null;
  }, [ingredient?.ingredient_id, inventoryIngredients]);

  const formats = linkedIngredient?.formats || [];
  const selectedFormat = ingredient?.format_id
    ? formats.find(f => f.format_id === ingredient.format_id)
    : null;

  // Compute live cost when linked (centralized helper, see lib/units.js)
  const liveCost = useMemo(
    () => (linkedIngredient ? computeIngredientCost(ingredient, linkedIngredient) : null),
    [linkedIngredient, ingredient]
  );

  const handleSelectIngredient = (inv) => {
    const baseUnit = inv.unit || "g";
    const defaultFormat = (inv.formats || []).find(f => f.is_default) || null;
    // When selecting, if there's a default format, pre-fill 1 unit of it.
    if (defaultFormat) {
      const qtyInBase = parseFloat(defaultFormat.qty_in_base_units) || 1;
      onChange(index, {
        ...ingredient,
        name: inv.name,
        ingredient_id: inv.ingredient_id,
        format_id: defaultFormat.format_id,
        format_qty: 1,
        format_label: `${defaultFormat.label} · ${defaultFormat.qty_in_base_units} ${baseUnit}`,
        quantity: qtyInBase,
        unit: baseUnit,
        estimated_cost: (parseFloat(inv.cost_per_unit) || 0) * qtyInBase,
      });
    } else {
      onChange(index, {
        ...ingredient,
        name: inv.name,
        ingredient_id: inv.ingredient_id,
        format_id: undefined,
        format_qty: undefined,
        format_label: undefined,
        unit: baseUnit,
        estimated_cost: (parseFloat(inv.cost_per_unit) || 0) * (parseFloat(ingredient.quantity) || 0),
      });
    }
    setOpen(false);
  };

  const handleUnlink = () => {
    onChange(index, {
      ...ingredient,
      ingredient_id: undefined,
      format_id: undefined,
      format_qty: undefined,
      format_label: undefined,
    });
  };

  const handleSelectFormat = (formatId) => {
    const fmt = formats.find(f => f.format_id === formatId);
    if (!fmt) return;
    const qtyInBase = parseFloat(fmt.qty_in_base_units) || 1;
    const formatQty = parseFloat(ingredient.format_qty) || 1;
    const baseUnit = linkedIngredient?.unit || "g";
    onChange(index, {
      ...ingredient,
      format_id: fmt.format_id,
      format_qty: formatQty,
      format_label: `${fmt.label} · ${fmt.qty_in_base_units} ${baseUnit}`,
      quantity: qtyInBase * formatQty,
      unit: baseUnit,
      estimated_cost: (parseFloat(linkedIngredient.cost_per_unit) || 0) * qtyInBase * formatQty,
    });
  };

  const handleFormatQtyChange = (v) => {
    const formatQty = parseFloat(v) || 0;
    const qtyInBase = parseFloat(selectedFormat?.qty_in_base_units) || 1;
    onChange(index, {
      ...ingredient,
      format_qty: formatQty,
      quantity: qtyInBase * formatQty,
      estimated_cost: (parseFloat(linkedIngredient?.cost_per_unit) || 0) * qtyInBase * formatQty,
    });
  };

  // Apply a unit-mismatch suggestion: link to inventory using the
  // inventory's unit but KEEP the numeric quantity typed by the chef
  // (they confirm manually the unit change — no silent conversion).
  const handleApplySuggestion = () => {
    if (!hasUnitMismatch) return;
    const inv = inventoryIngredients.find(i => i.ingredient_id === suggestion.inventory_ingredient_id);
    if (!inv) return;
    const nextRow = {
      ...ingredient,
      name: inv.name,
      ingredient_id: inv.ingredient_id,
      unit: inv.unit || suggestion.inventory_unit,
      _display_quantity: undefined,
      _display_unit: undefined,
    };
    onChange(index, {
      ...nextRow,
      estimated_cost: computeIngredientCost(nextRow, inv),
    });
    if (typeof onDismissSuggestion === "function") onDismissSuggestion();
  };

  // Unit-mismatch badge (shared between read-only and edit views)
  const unitMismatchBadge = hasUnitMismatch ? (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-medium bg-amber-50 text-amber-800 border border-amber-300 hover:bg-amber-100 transition-colors dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-900 dark:hover:bg-amber-950/60"
          data-testid={`recipe-ingredient-unit-mismatch-${index}`}
          title="Unidad incompatible con el inventario"
        >
          <AlertTriangle className="w-2.5 h-2.5" /> Revisar unidad
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-3" align="start">
        <div className="space-y-2">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-700 flex-shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="font-medium">Unidad incompatible</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                La receta usa <span className="font-mono font-semibold">{suggestion.recipe_unit || "?"}</span> pero{" "}
                <span className="font-medium">{suggestion.inventory_name}</span> está en{" "}
                <span className="font-mono font-semibold">{suggestion.inventory_unit || "?"}</span>. No podemos convertir automáticamente.
              </p>
            </div>
          </div>
          <div className="flex flex-col gap-1.5 pt-1">
            <Button
              size="sm"
              className="w-full h-8 text-xs"
              onClick={handleApplySuggestion}
              data-testid={`recipe-ingredient-apply-suggestion-${index}`}
            >
              <Link2 className="w-3 h-3 mr-1" />
              Vincular usando {suggestion.inventory_unit || "unidad del inventario"}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="w-full h-7 text-xs text-muted-foreground"
              onClick={() => typeof onDismissSuggestion === "function" && onDismissSuggestion()}
              data-testid={`recipe-ingredient-dismiss-suggestion-${index}`}
            >
              <X className="w-3 h-3 mr-1" /> Ignorar
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  ) : null;

  // ── Read-only view ────────────────────────────────────────────────────
  if (!isEditing) {
    return (
      <div className="flex items-center gap-2 p-3 bg-muted/50 rounded-lg" data-testid={`recipe-ingredient-row-${index}`}>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium truncate">{ingredient.name}</span>
            {ingredient.ingredient_id && (
              <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-900" data-testid="recipe-ingredient-linked-badge">
                <Link2 className="w-2.5 h-2.5 mr-1" /> Vinculado
              </Badge>
            )}
            {unitMismatchBadge}
          </div>
          {ingredient.format_label && (
            <p className="text-xs text-muted-foreground mt-0.5">
              {ingredient.format_qty || 1} × {ingredient.format_label}
            </p>
          )}
        </div>
        <span className="font-mono text-sm text-muted-foreground" title={ingredient._display_quantity ? `Equivale a ${ingredient.quantity} ${ingredient.unit} (base)` : undefined}>
          {ingredient._display_quantity ?? ingredient.quantity} {ingredient._display_unit ?? ingredient.unit}
        </span>
        {ingredient.estimated_cost > 0 && (
          <Badge variant="outline">{formatCurrency(ingredient.estimated_cost)}</Badge>
        )}
      </div>
    );
  }

  // ── Editable view ─────────────────────────────────────────────────────
  return (
    <div className="space-y-2 p-3 bg-muted/30 rounded-lg border border-border/40" data-testid={`recipe-ingredient-row-${index}`}>
      {unitMismatchBadge && (
        <div className="flex items-center justify-start -mb-1">
          {unitMismatchBadge}
        </div>
      )}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
        {/* Ingredient name / link */}
        <div className="flex-1 min-w-0 flex gap-2 items-center">
          {linkedIngredient ? (
            <div className="flex-1 flex items-center gap-2 px-3 py-1.5 rounded-md bg-emerald-50/60 dark:bg-emerald-950/30 border border-emerald-200/60 dark:border-emerald-900/60">
              <Package className="w-4 h-4 text-emerald-700 flex-shrink-0" />
              <span className="text-sm font-medium truncate">{linkedIngredient.name}</span>
              <Badge variant="outline" className="text-[10px] bg-emerald-100 text-emerald-700 border-emerald-300 dark:bg-emerald-900/40 dark:text-emerald-300 dark:border-emerald-800 flex-shrink-0">
                <Link2 className="w-2.5 h-2.5 mr-1" /> Inventario
              </Badge>
              <Button
                size="icon" variant="ghost"
                className="h-7 w-7 flex-shrink-0"
                onClick={handleUnlink}
                title="Desvincular"
                data-testid={`recipe-ingredient-unlink-${index}`}
              >
                <Link2Off className="w-3.5 h-3.5 text-muted-foreground" />
              </Button>
            </div>
          ) : (
            <>
              <Input
                value={ingredient.name || ""}
                onChange={(e) => onChange(index, { ...ingredient, name: e.target.value })}
                placeholder="Ingrediente"
                className="flex-1"
                data-testid={`recipe-ingredient-name-input-${index}`}
              />
              <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                  <Button
                    type="button" variant="outline" size="sm"
                    className="h-9 gap-1.5 flex-shrink-0"
                    data-testid={`recipe-ingredient-link-btn-${index}`}
                  >
                    <Link2 className="w-3.5 h-3.5" />
                    <span className="hidden sm:inline">Vincular</span>
                    <ChevronsUpDown className="w-3 h-3 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[320px] p-0" align="end">
                  <Command>
                    <CommandInput placeholder="Buscar ingrediente…" data-testid="recipe-ingredient-search-input" />
                    <CommandList>
                      <CommandEmpty>
                        <div className="py-4 text-center space-y-2">
                          <p className="text-sm text-muted-foreground">Sin resultados</p>
                          {onQuickAdd && (
                            <Button
                              type="button" size="sm" variant="outline"
                              className="gap-1.5"
                              onClick={() => { setOpen(false); onQuickAdd(ingredient.name || ""); }}
                              data-testid="recipe-ingredient-quick-add-btn"
                            >
                              <Plus className="w-3.5 h-3.5" /> Crear en inventario
                            </Button>
                          )}
                        </div>
                      </CommandEmpty>
                      <CommandGroup heading="Ingredientes del inventario">
                        {inventoryIngredients.slice(0, 100).map(inv => (
                          <CommandItem
                            key={inv.ingredient_id}
                            value={inv.name}
                            onSelect={() => handleSelectIngredient(inv)}
                            className="flex items-center gap-2"
                            data-testid={`recipe-ingredient-option-${inv.ingredient_id}`}
                          >
                            <Check className={cn("w-4 h-4", linkedIngredient?.ingredient_id === inv.ingredient_id ? "opacity-100" : "opacity-0")} />
                            <span className="flex-1 truncate">{inv.name}</span>
                            <span className="text-[11px] text-muted-foreground">
                              {formatCurrency(parseFloat(inv.cost_per_unit) || 0)}/{inv.unit}
                            </span>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </>
          )}
        </div>

        {/* Remove */}
        <Button
          size="icon" variant="ghost"
          onClick={() => onRemove(index)}
          className="h-9 w-9 flex-shrink-0 self-end sm:self-auto"
          data-testid={`recipe-ingredient-remove-${index}`}
        >
          <Trash2 className="w-4 h-4 text-destructive" />
        </Button>
      </div>

      {/* Second row — quantity & unit (or format selector when linked with formats) */}
      <div className="flex items-center gap-2 pl-1">
        {linkedIngredient && formats.length > 0 ? (
          <>
            <Input
              type="number" min={0} step={0.1}
              value={ingredient.format_qty ?? 1}
              onChange={(e) => handleFormatQtyChange(e.target.value)}
              className="w-20"
              data-testid={`recipe-ingredient-format-qty-${index}`}
            />
            <Select value={ingredient.format_id || ""} onValueChange={handleSelectFormat}>
              <SelectTrigger className="flex-1" data-testid={`recipe-ingredient-format-select-${index}`}>
                <SelectValue placeholder="Elegir formato…" />
              </SelectTrigger>
              <SelectContent>
                {formats.map(f => (
                  <SelectItem key={f.format_id} value={f.format_id} data-testid={`recipe-ingredient-format-option-${f.format_id}`}>
                    {f.label} · {f.qty_in_base_units} {linkedIngredient.unit}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <span className="text-xs text-muted-foreground font-mono">
              = {ingredient.quantity} {ingredient.unit}
            </span>
          </>
        ) : (
          <>
            <Input
              type="number" min={0} step={0.1}
              value={ingredient.quantity ?? 0}
              onChange={(e) => {
                const q = parseFloat(e.target.value) || 0;
                const cost = linkedIngredient
                  ? (parseFloat(linkedIngredient.cost_per_unit) || 0) * q
                  : ingredient.estimated_cost;
                onChange(index, { ...ingredient, quantity: q, estimated_cost: cost });
              }}
              className="w-24"
              data-testid={`recipe-ingredient-qty-${index}`}
            />
            <Input
              value={ingredient.unit || ""}
              onChange={(e) => onChange(index, { ...ingredient, unit: e.target.value })}
              placeholder="Unidad" className="w-20"
              disabled={!!linkedIngredient}
              data-testid={`recipe-ingredient-unit-${index}`}
            />
            {!linkedIngredient && (
              <Input
                type="number" min={0} step={0.01}
                value={ingredient.estimated_cost ?? 0}
                onChange={(e) => onChange(index, { ...ingredient, estimated_cost: parseFloat(e.target.value) || 0 })}
                className="w-24" placeholder="€"
                data-testid={`recipe-ingredient-cost-${index}`}
              />
            )}
          </>
        )}
      </div>

      {linkedIngredient && liveCost != null && (
        <div className="flex items-center justify-between pl-1 text-xs">
          <span className="text-muted-foreground">
            Coste real: <span className="font-semibold text-emerald-700 dark:text-emerald-400">{formatCurrency(liveCost)}</span>
          </span>
          <span className="text-muted-foreground">
            {formatCurrency(parseFloat(linkedIngredient.cost_per_unit) || 0)}/{linkedIngredient.unit}
            {parseFloat(linkedIngredient.waste_percentage) > 0 && ` · merma ${linkedIngredient.waste_percentage}%`}
          </span>
        </div>
      )}
    </div>
  );
}
